import { useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { FieldError } from '../../../../../components/novarium/NvTableParts';
import { SelectOption } from '../../../types/billing.types';

export interface NewTax {
  taxId: number;
  taxName: string;
  amount: number;
  description: string;
}

interface TaxDialogProps {
  taxTypes: SelectOption[];
  /** Impuestos ya cargados en este cobro: cada uno va una sola vez. */
  usedTaxIds: number[];
  onCancel: () => void;
  onAdd: (tax: NewTax) => void;
}

type Errors = Partial<Record<'taxId' | 'amount' | 'description', string>>;

/** "Nuevo impuesto", como {@code TaxWindow}: impuesto, importe y descripción. */
export default function TaxDialog({ taxTypes, usedTaxIds, onCancel, onAdd }: TaxDialogProps) {
  const [taxId, setTaxId] = useState<number | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Errors>({});

  const add = () => {
    const next: Errors = {};
    if (taxId == null) next.taxId = 'Elegí el impuesto';
    else if (usedTaxIds.includes(taxId)) next.taxId = 'Este impuesto ya está cargado';
    if (amount == null || amount <= 0) next.amount = 'Ingresá el importe del impuesto';
    if (!description.trim()) next.description = 'Ingresá la descripción del impuesto';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onAdd({ taxId: taxId!, taxName: taxTypes.find((t) => t.id === taxId)?.name ?? '', amount: amount!, description: description.trim() });
  };

  const clear = (field: keyof Errors) => setErrors((e) => ({ ...e, [field]: undefined }));

  return (
    <Dialog
      header="Nuevo impuesto"
      visible
      onHide={onCancel}
      className="nv-scope nv-dialog"
      modal
      draggable={false}
      footer={(
        <>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onCancel} />
          <Button type="button" className="buttonMain" label="Agregar" onClick={add} />
        </>
      )}
    >
      <div className="nv-dialog-body">
        <div className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="tax-type">Impuesto <span className="nv-req">*</span></label>
            <Dropdown panelClassName="nv-scope" inputId="tax-type" value={taxId} options={taxTypes} optionLabel="name" optionValue="id" placeholder="Seleccionar" filter
              onChange={(e) => { setTaxId(e.value ?? null); clear('taxId'); }} className={errors.taxId ? 'p-invalid' : undefined} />
            <FieldError id="tax-type-error" message={errors.taxId} />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="tax-amount">Importe <span className="nv-req">*</span></label>
            <InputNumber inputId="tax-amount" value={amount} mode="currency" currency="ARS" locale="es-AR" minFractionDigits={2}
              maxFractionDigits={2} min={0} onValueChange={(e) => { setAmount(e.value ?? null); clear('amount'); }}
              className={errors.amount ? 'p-invalid' : undefined} />
            <FieldError id="tax-amount-error" message={errors.amount} />
          </div>
        </div>
        <div className="nv-field nv-w-l">
          <label htmlFor="tax-description">Descripción <span className="nv-req">*</span></label>
          <InputText id="tax-description" value={description} maxLength={255} placeholder="Ej.: Retención de Ganancias"
            onChange={(e) => { setDescription(e.target.value); clear('description'); }} className={errors.description ? 'p-invalid' : undefined} />
          <FieldError id="tax-description-error" message={errors.description} />
        </div>
      </div>
    </Dialog>
  );
}
